import { AgGridReact } from "@jsq/ds/core";
import { type GlSyncError } from "~/pages/FundAccounting/utils/columnDefs";
import { type SubSyncStatus } from "~/pages/FundAccounting/utils/glSyncStatus";
import { formatDate } from "~/pages/FundAccounting/utils/helpers";
import BatchStatusRenderer, { Props } from "./components/StatusRenderer";

export const colDefs: AgGridReact.AgGridReactProps["columnDefs"] = [
  {
    field: "batchDate",
    headerName: "Transfer date",
    sortable: false,
    maxWidth: 150,
  },
  {
    field: "description",
    headerName: "Description",
    sortable: false,
    minWidth: 400,
  },
  {
    field: "status",
    headerName: "Status",
    maxWidth: 220,
    cellRenderer: (params: Props) => <BatchStatusRenderer {...params} />,
  },
];

export const gridOptions = (
  arenaName = "",
  arenaSubdomain = "",
  entityId = -1,
  entitySettingsNotRestrict = false,
): AgGridReact.AgGridReactProps["gridOptions"] => {
  return {
    autoSizeStrategy: {
      type: "fitGridWidth",
    },
    suppressCellFocus: true,
    rowHeight: 42,
    pagination: true,
    paginationPageSize: 15,
    paginationPageSizeSelector: false,
    domLayout: "autoHeight",
    context: {
      arenaName,
      entityId,
      arenaSubdomain,
      entitySettingsNotRestrict,
    },
  };
};

export type EntityTransferDatePositionMap = {
  [key: string]: Set<number>;
};

export enum BatchRowStatus {
  READY = "Ready to sync",
  PROCESSING = "Processing",
  NOT_READY = "Unable to sync",
  FAILED = "Sync failed",
  COMPLETE = "Synced",
}

export const getRowData = (data: Row[], entitySettingsNotRestrict = false) => {
  return data.map((row) => {
    const status = entitySettingsNotRestrict ? BatchRowStatus.NOT_READY : row.status;
    return {
      ...row,
      batchDate: formatDate(row.batchDate),
      description: row.description,
      status: {
        positionId: row.positionIds[0],
        status: status,
        // EQ-4892: raw backend sync status drives the descriptive step + spinner.
        rawSyncStatus: row.rawSyncStatus,
        syncError: row.syncError,
      },
    };
  });
};

export type Row = {
  batchDate: string;
  description: string;
  positionIds: number[];
  status?: BatchRowStatus;
  rawSyncStatus?: SubSyncStatus;
  syncError?: GlSyncError | null;
};

export const checkTransferEvents = (
  data: Row[],
  transferDatePositionMap: EntityTransferDatePositionMap,
) => {
  data.forEach((row) => {
    const positionIds = row.positionIds;
    const batchDate = row.batchDate;
    if (!(
      batchDate in transferDatePositionMap &&
      positionIds.every((positionId) => transferDatePositionMap[batchDate].has(positionId))
    )) {
      row.status = BatchRowStatus.NOT_READY;
    }
  });
  return data;
};

type DeltaObject = {
  fromPositionId?: number | null;
  toPositionId?: number | null;
  date?: string | null;
};

export const populatePositionTransferDateMap = (
  deltas: DeltaObject[],
): EntityTransferDatePositionMap => {
  const datePositionMap: EntityTransferDatePositionMap = {};
  deltas.forEach((delta) => {
    if (!delta.date || !delta.fromPositionId || !delta.toPositionId) {
      return;
    }

    if (!datePositionMap[delta.date.toString()]) {
      datePositionMap[delta.date.toString()] = new Set();
    }
    datePositionMap[delta.date.toString()].add(delta.fromPositionId);
    datePositionMap[delta.date.toString()].add(delta.toPositionId);
  });
  return datePositionMap;
};
